import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import useFormSchema from "../formModels/useFormSchema";
import SimpleTextField from "./SimpleTextField";
import LanguageValueField from "./LanguageValueField";
import LanguageValueList from "../formModels/LanguageValueList";
import { getTitleByField, getDescriptionByField, parseValidationErrors, cleanEmptyFields } from "../config/commonFunctions";
import { Stack, Button, Typography, Paper, Snackbar, Alert, Dialog, DialogTitle, DialogContent} from "@mui/material";
import ErrorIcon from '@mui/icons-material/Error';
import { PARTICIPANT_CONNECTORS, PARTICIPANT_FORM_CONNECTOR_SCHEMA } from "../config/settings";

const initialFormData = {
    "name": "",
    "description": new LanguageValueList(),
    "certificate": "",
    "connector_url": "",
};

const ParticipantConnectorForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const editingConnector = location.state?.connector || null;
    const [isEditMode, setIsEditMode] = useState(false); // To track edit mode
    const [connectorId, setConnectorId] = useState(null); // For the PUT request
    const [globalLanguage, setGlobalLanguage] = useState("en"); // State for global language
    const [schemaAsObject, setSchemaAsObject] = useState({});
    const [dataToSave, setDataToSave] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [validationError, setValidationError] = useState(null);
    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [open, setOpen] = useState(false);

    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") {
          return;
        }
        setSnackbarOpen(false);
      };

      const handleSnackbarMessage = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
      };
    const isFieldRequired = (field) => {
        // Check if required array exists in the schema
        return schemaAsObject?.required?.includes(field) ?? false;
    };

    const handleClickOpen = () => {
        setOpen(true);
      };

      const handleClose = (value) => {
        setOpen(false);
      };

    const {
        formData,
        handleChange,
        handleFieldChange,
        handleFileUpload,
    } = useFormSchema(initialFormData, null, handleSnackbarMessage, () => setValidationError(null));


    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true); // Set loading state
                const url = PARTICIPANT_FORM_CONNECTOR_SCHEMA + globalLanguage + "/";
                const result = await axios.get(url);
                setSchemaAsObject(result.data);
                // console.log("Fetched Schema:", result.data);
                setIsLoading(false);
            } catch (err) {
                console.error("Error fetching schema:", err);
                setIsLoading(false);
            }
        };
        fetchData();
    }, [globalLanguage]);


    // Check if we're in edit mode by looking for connector data in location.state
    useEffect(() => {
        if (location.state?.connector) {
            setIsEditMode(true);
            setConnectorId(location.state.connector.id); // Store the connector ID for the PUT request
            // console.log("connectorEDIT", location.state.connector);
            // Prefill formData with the connector's data
            const prepopulateFormData = {
                name: editingConnector.name,
                certificate : editingConnector.certificate,
                connector_url: editingConnector.connector_url,
                description: new LanguageValueList(editingConnector.description),
            };
            // console.log("prepopulateFormData", prepopulateFormData);
            // Update the formData in useFormSchema hook with the pre-populated data
            handleFieldChange("name", prepopulateFormData.name);
            handleFieldChange("description", prepopulateFormData.description);
            handleChange("connector_url", prepopulateFormData.connector_url);
            handleChange("certificate", prepopulateFormData.certificate);
        }
    }, [editingConnector]);

    // Function to serialize formData into a simple JSON object
    const serializeFormData = (currentFormData) => {
        const serializedData = {
            name: currentFormData.name, // Converts to array of language-value pairs
            connector_url: currentFormData.connector_url,
            certificate: currentFormData.certificate,
            description: currentFormData.description.toJSON(),
        };
        return serializedData;
    };

    const handleSubmit = () => {
        setValidationError(null);   // clear previous errors before new request
        const finalToSave = serializeFormData(formData);
        const cleanedData = cleanEmptyFields(finalToSave);
        // console.log("Data to save:", cleanedData);
        if (isEditMode) {
            // PUT request for editing existing connector
            axios
                .put(`${PARTICIPANT_CONNECTORS}${connectorId}/`, cleanedData)
                .then((response) => {
                    // console.log("Connector updated", response);
                    navigate("/management"); // Navigate back to the connector home
                })
                .catch((err) => {
                    if (err.response && err.response.status === 400) {
                        setValidationError(err.response.data);
                    } else {
                        console.error("Unexpected error occurred:", err);
                    }
                });
        } else {
            // POST request for creating a new connector
            axios
                .post(PARTICIPANT_CONNECTORS, cleanedData)
                .then((response) => {
                    // console.log("Connector created", response);
                    navigate("/management"); // Redirect to the submission success page
                })
                .catch((err) => {
                    if (err.response && err.response.status === 400) {
                        setValidationError(err.response.data);
                    } else {
                        console.error("Unexpected error occurred:", err);
                    }
                });
        }
    };

    function printError(errorMessages) {
        if (!errorMessages) {
            return <></>;
        }
        const errors = parseValidationErrors(errorMessages);
        // Split the errors string into an array by newline characters
        const errorLines = errors.split("\n").filter((line) => line.trim() !== "");
        return (
            <Paper style={{ padding: "1%", marginTop: "1em" }}>
                <div className="section--title--warning flex flex--center">
                    <span><ErrorIcon className="nav-icon-xlarge" /></span>
                    <span><Typography variant="h6" noWrap>Validation notices</Typography></span>
                </div>
                <div>
                    {errorLines.map((error, index) => (
                        <div key={index} style={{paddingBottom: "0.5em"}}>{error}</div> // Each error rendered in its own div
                    ))}
                </div>
            </Paper>
        );
    }
    // console.log(editingConnector);
    return (<>
        <Stack spacing={2}>
            <div>
                <Typography variant="h3" sx={{ mb: 1, mt: 5 }}>
                    Connector Registration Form
                </Typography>
                <Typography variant="h6" sx={{ mb: 3, mt: 1 }}>
                    Add the details of your connector.
                </Typography>
            </div>
            <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <SimpleTextField
                        label={getTitleByField(schemaAsObject, "name")}
                        helperText={getDescriptionByField(schemaAsObject, "name")}
                        required={isFieldRequired("name")}
                        value={formData?.name}
                        onChange={(value) => handleChange("name", value)}
                    />

                    <LanguageValueField
                        id={"description-id"}
                        label={getTitleByField(schemaAsObject, "description")}
                        helperText={getDescriptionByField(schemaAsObject, "description")}
                        multiline={true}
                        minRows={3}
                        obj={formData?.description}
                        lang={globalLanguage}
                        required={isFieldRequired("description")}
                        onChange={(updatedValues) => handleFieldChange("description", updatedValues)}
                    />

                    <div style={{ paddingTop: "1em", width: "max-content" }}>
                        <Typography variant="body2" gutterBottom>Certificate {isFieldRequired("certificate") && "*"}</Typography>

                        {isEditMode && editingConnector?.certificate ? (<>
                            <div style={{ marginBottom: "1em" }}>
                                <Typography variant="p" gutterBottom sx={{ fontStyle: "italic", color: "green" }}>
                                    Certificate is already uploaded. You can upload a new one to replace it.
                                    You can view your certificate <span style={{cursor: "pointer", fontWeight: "600"}} variant="outlined" onClick={handleClickOpen}>here.</span>
                                </Typography>
                            </div>
                            <Dialog onClose={handleClose} open={open}>
                                <DialogTitle>Uploaded certificate</DialogTitle>
                                <DialogContent>
                                    <div>
                                    <Typography variant="body1" sx={{fontWeight: 600}}>Current certificate</Typography>
                                    <Typography variant="caption">{editingConnector.certificate}</Typography>
                                    </div>
                                    {editingConnector?.certificate_sha256 && <div style={{paddingTop: "1em"}}>
                                    <Typography variant="body1" sx={{fontWeight: 600}}>Current certificate digest value</Typography>
                                    <Typography variant="caption">{editingConnector?.certificate_sha256}</Typography>
                                    </div>}
                                </DialogContent>
                            </Dialog>
                            </>
                        ) : null}
                        <input
                            type="file"
                            // accept=".txt"
                            onChange={handleFileUpload}
                            required
                            style={{
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "12px",
                                fontSize: "16px",
                                width: "100%"
                            }}
                        />
                        <span style={{fontSize: "0.75rem", color: "rgba(0, 0, 0, 0.6)"}}>{getDescriptionByField(schemaAsObject, "certificate")}</span>
                    </div>
                    <div style={{ paddingTop: "1em" }}>
                        <SimpleTextField
                            label={getTitleByField(schemaAsObject, "connector_url")}
                            helperText={getDescriptionByField(schemaAsObject, "connector_url")}
                            required={isFieldRequired("connector_url")}
                            value={formData?.connector_url}
                            onChange={(value) => handleChange("connector_url", value)}
                        />
                    </div>
                </Stack>
            </Paper>

            {validationError && <Paper>
                <Stack sx={{ mt: 2 }}>{printError(validationError)}</Stack>
            </Paper>}

            <Button onClick={handleSubmit} variant="contained" color="success">
                {isEditMode ? "Update" : "Submit"}
            </Button>
        </Stack>

        {/* Snackbar Component */}
        <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
        >
            <Alert
                onClose={handleSnackbarClose}
                severity={snackbarSeverity}
                sx={{ width: "100%" }}
            >
                {snackbarMessage}
            </Alert>
        </Snackbar>

    </>
    );
};

export default ParticipantConnectorForm;
